/**
 * Wishlist + recently viewed products (stored per browser in localStorage).
 *
 * Used by snippets/wishlist-button.liquid, sections/wishlist-products.liquid and
 * sections/recently-viewed-product.liquid. Storage keys and format (arrays of
 * product handles) are kept from the original theme so existing shoppers keep
 * their lists: `wishlistItems`, `recentlyViewedProducts`.
 *
 * Lists are rendered with the theme's real product card, fetched per product from
 * templates/product.list-card.liquid, so prices and availability are always live.
 *
 * Config: window.MangofyListsConfig (layout/theme.liquid).
 * API: window.MangofyLists.get('wishlist' | 'recent'), .toggleWishlist(handle), .trackView(handle)
 * Event: 'mangofy:lists-change' on document, detail { list, handles }.
 */
(function () {
  if (window.MangofyLists) return;

  var config = window.MangofyListsConfig || {};
  var KEYS = { wishlist: 'wishlistItems', recent: 'recentlyViewedProducts' };
  var CHANGE_EVENT = 'mangofy:lists-change';
  var WISHLIST_LIMIT = 60;
  var cardCache = {};

  function read(list) {
    try {
      var value = JSON.parse(localStorage.getItem(KEYS[list]) || '[]');
      return Array.isArray(value)
        ? value.filter(function (handle) {
            return typeof handle === 'string' && handle !== '';
          })
        : [];
    } catch (error) {
      return [];
    }
  }

  function save(list, handles) {
    try {
      localStorage.setItem(KEYS[list], JSON.stringify(handles));
    } catch (error) {
      /* storage full or blocked: lists simply stay empty */
    }
  }

  function write(list, handles) {
    save(list, handles);
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list: list, handles: handles } }));
  }

  var api = {
    get: read,

    toggleWishlist: function (handle) {
      var items = read('wishlist');
      var index = items.indexOf(handle);
      if (index === -1) items.unshift(handle);
      else items.splice(index, 1);
      write('wishlist', items.slice(0, WISHLIST_LIMIT));
      return index === -1;
    },

    trackView: function (handle) {
      var items = read('recent').filter(function (item) {
        return item !== handle;
      });
      items.unshift(handle);
      write('recent', items.slice(0, Math.max(1, config.recentLimit || 8)));
    },
  };

  /* ---------- Wishlist buttons + header count ---------- */

  var liveRegion = null;
  function announce(message) {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden-live';
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(function () {
      liveRegion.textContent = message;
    }, 50);
  }

  function syncWishlistUI(root) {
    var items = read('wishlist');
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
      var saved = items.indexOf(button.dataset.wishlistToggle) !== -1;
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    });
    if (!root) syncHeaderCount(items.length);
  }

  // Header link mirrors the cart link: "(n) Wishlist" on desktop, bubble on mobile.
  function syncHeaderCount(count) {
    var shown = count > 99 ? '99+' : String(count);
    document.querySelectorAll('[data-wishlist-count]').forEach(function (bubble) {
      bubble.textContent = shown;
      bubble.hidden = count === 0;
    });
    document.querySelectorAll('[data-wishlist-count-text]').forEach(function (text) {
      text.textContent = '(' + shown + ') ';
    });
    document.querySelectorAll('[data-wishlist-label]').forEach(function (label) {
      label.classList.toggle('text-secondary', count > 0);
    });
    document.querySelectorAll('.header-wishlist').forEach(function (link) {
      var name = link.getAttribute('title') || 'Wishlist';
      link.setAttribute('aria-label', count ? name + ' (' + shown + ')' : name);
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    var added = api.toggleWishlist(button.dataset.wishlistToggle);
    announce(added ? (config.strings || {}).added : (config.strings || {}).removed);
  });

  /* ---------- Product lists ---------- */

  function fetchCard(handle) {
    if (cardCache[handle]) return cardCache[handle];
    var url = (config.root || '/') + 'products/' + encodeURIComponent(handle) + '?view=list-card';
    cardCache[handle] = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (res.status === 404) return { gone: true };
        return res.ok ? res.text().then(function (html) { return { html: html }; }) : {};
      })
      .then(function (result) {
        // Guard against a full page coming back (e.g. template missing): only accept a card fragment.
        if (result.html && (result.html.indexOf('product-card-wrapper') === -1 || /<html[\s>]/i.test(result.html))) return {};
        return result;
      })
      .catch(function () {
        delete cardCache[handle];
        return {};
      });
    return cardCache[handle];
  }

  class CustomerProductList extends HTMLElement {
    connectedCallback() {
      this.list = this.dataset.list === 'wishlist' ? 'wishlist' : 'recent';
      this.section = this.closest('[data-customer-list-section]') || this;
      this.grid = this.querySelector('[data-list-grid]');
      this.empty = this.querySelector('[data-list-empty]');
      this.hint = this.querySelector('[data-list-editor-hint]');
      this.renderId = 0;
      this.onChange = this.onChange.bind(this);
      document.addEventListener(CHANGE_EVENT, this.onChange);
      window.addEventListener('storage', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
      window.removeEventListener('storage', this.onChange);
    }

    onChange(event) {
      var list = event.detail ? event.detail.list : event.key === KEYS.wishlist ? 'wishlist' : event.key === KEYS.recent ? 'recent' : null;
      if (list === this.list) this.render();
      else syncWishlistUI(this.grid);
    }

    handles() {
      var exclude = this.dataset.exclude;
      var limit = parseInt(this.dataset.limit, 10) || 8;
      return read(this.list)
        .filter(function (handle) {
          return handle !== exclude;
        })
        .slice(0, limit);
    }

    setState(state) {
      // state: 'items' | 'empty'
      var hideWhenEmpty = this.dataset.hideEmpty === 'true';
      var designMode = window.Shopify && window.Shopify.designMode;
      var isEmpty = state === 'empty';
      this.grid.hidden = isEmpty;
      if (this.empty) this.empty.hidden = !isEmpty || (hideWhenEmpty && !designMode);
      if (this.hint) this.hint.hidden = !(isEmpty && designMode);
      this.section.hidden = isEmpty && hideWhenEmpty && !designMode;
    }

    render() {
      var self = this;
      var renderId = ++this.renderId;
      var handles = this.handles();
      if (!handles.length) return this.setState('empty');

      Promise.all(handles.map(fetchCard)).then(function (results) {
        if (renderId !== self.renderId) return;
        var gone = [];
        var html = '';
        results.forEach(function (result, index) {
          if (result.gone) gone.push(handles[index]);
          if (result.html) html += '<li class="customer-list__item">' + result.html + '</li>';
        });
        if (gone.length) {
          // Deleted / unpublished products: drop them quietly (no change event, avoids re-render loops).
          save(self.list, read(self.list).filter(function (handle) {
            return gone.indexOf(handle) === -1;
          }));
        }
        if (!html) return self.setState('empty');
        self.grid.innerHTML = html;
        syncWishlistUI(self.grid);
        self.setState('items');
      });
    }
  }

  /* ---------- Init ---------- */

  window.MangofyLists = api;
  if (!customElements.get('customer-product-list')) customElements.define('customer-product-list', CustomerProductList);

  document.addEventListener(CHANGE_EVENT, function () {
    syncWishlistUI();
  });
  window.addEventListener('storage', function (event) {
    if (event.key === KEYS.wishlist) syncWishlistUI();
  });

  function init() {
    if (config.recentEnabled) {
      var tracker = document.querySelector('[data-recently-viewed-track]');
      if (tracker && tracker.dataset.recentlyViewedTrack) api.trackView(tracker.dataset.recentlyViewedTrack);
    }
    syncWishlistUI();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
